import { Reducer, Collection } from 'interfaces'
import { FloatingPanesState, TransactionRecatFloatingPane, FloatingPane, MixedAccountDetailsPane } from 'interfaces/floatingPanes'
import { removeByKeyFromCollection, setItemInCollection, createEmptyCollection, getItemFromCollection } from 'helpers/normalizeHelpers'
import { Action } from 'actions'
import { KILL_ALL_FLOATING_PANES, KILL_FLOATING_PANES, MODIFY_FLOATING_PANE } from 'actions/floatingPanesActions'
import { GET_SIMILAR_TRANSACTIONS } from 'actions/moneyflowActions'
import { createEmptyFetchable, createFetchableReducer } from 'helpers/loadableHelpers'
import { Transaction } from 'interfaces/moneyflow'
import { combineReducers } from 'redux'
import { isSuccessAction } from 'helpers/actionsHelpers'
import { START_LINKAGE_SESSION, AUTHENTICATE_LINKAGE, KILL_LINKAGE_SESSION } from 'actions/linkageActions'

const DEFAULT_TRANSACTION_RECAT_PANE: TransactionRecatFloatingPane = {
  type: 'transaction-recat',
  mode: 'category',
  selectedTransactionUids: [],
  similarTransactions: createEmptyFetchable(),
  transaction: null,
  createRuleWasAutochecked: false,
}

const similarTransactionsReducer = createFetchableReducer<Collection<Transaction, 'uid'>>()

const INITIAL_STATE = createEmptyCollection<FloatingPane, 'type'>()

const panesReducer: Reducer<FloatingPanesState['panes']> = (prevState = INITIAL_STATE, action: Action) => {
  switch (action.type) {
    case KILL_ALL_FLOATING_PANES: {
      return createEmptyCollection()
    }

    case KILL_FLOATING_PANES: {
      return removeByKeyFromCollection(prevState, ...action.payload.types)
    }

    case MODIFY_FLOATING_PANE: {
      let nextPane = action.payload
      const prevPane = getItemFromCollection(prevState, 'transaction-recat')
      if (nextPane.type === 'transaction-recat' && !prevPane) {
        nextPane = {
          ...DEFAULT_TRANSACTION_RECAT_PANE,
          ...nextPane,
        }
      }

      return setItemInCollection(prevState, nextPane, 'type', { mergeIfExist: true })
    }

    case GET_SIMILAR_TRANSACTIONS: {
      const prevPane = getItemFromCollection(prevState, 'transaction-recat') as TransactionRecatFloatingPane | null
      if (!prevPane) {
        return prevState
      }

      const nextSimilarTransactions = similarTransactionsReducer(prevPane.similarTransactions, action)
      if (nextSimilarTransactions !== prevPane.similarTransactions) {
        const nextPane: TransactionRecatFloatingPane = {
          ...prevPane,
          similarTransactions: nextSimilarTransactions,
        }

        return setItemInCollection(prevState, nextPane, 'type')
      }

      return prevState
    }

    case START_LINKAGE_SESSION: {
      if (isSuccessAction(action)) {
        const nextPane: MixedAccountDetailsPane = {
          type: 'mixed-account-details',
          sessionId: action.payload.response.sessionId,
        }

        return setItemInCollection(prevState, nextPane, 'type')
      }

      return prevState
    }

    case KILL_LINKAGE_SESSION: {
      const detailsPane = getItemFromCollection(prevState, 'mixed-account-details') as MixedAccountDetailsPane | null
      if (detailsPane && detailsPane.sessionId === action.payload.sessionId) {
        return removeByKeyFromCollection(prevState, 'mixed-account-details')
      }

      return prevState
    }

    case AUTHENTICATE_LINKAGE: {
      if (isSuccessAction(action)) {
        return createEmptyCollection<FloatingPane, 'type'>()
      }

      return prevState
    }
  }

  return prevState
}

export const floatingPanesReducer = combineReducers<FloatingPanesState>({
  panes: panesReducer,
})


